import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Monitor, Plus, AlertTriangle, CheckCircle, Clock, TrendingUp, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = "blue", subtitle }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600", 
    red: "bg-red-50 text-red-600",
    yellow: "bg-yellow-50 text-yellow-600"
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`p-2 sm:p-3 rounded-lg ${colorClasses[color]} flex-shrink-0`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-3 sm:ml-4 min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs sm:text-sm text-gray-500 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

// Recent Incidents Component
const RecentIncidents = ({ incidents }) => {
  if (!incidents || incidents.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Recent Incidents</h3>
        <div className="text-center py-6 sm:py-8">
          <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-600 text-sm sm:text-base">No recent incidents. All systems running smoothly!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Recent Incidents</h3>
      <div className="space-y-3 sm:space-y-4">
        {incidents.map((incident) => (
          <div key={incident.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className={`p-1 rounded-full flex-shrink-0 ${
              incident.alert_type === 'down' ? 'bg-red-100 text-red-600' : 
              incident.alert_type === 'up' ? 'bg-green-100 text-green-600' :
              'bg-yellow-100 text-yellow-600'
            }`}>
              {incident.alert_type === 'down' ? 
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" /> :
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{incident.message}</p>
              <p className="text-xs text-gray-500">{new Date(incident.started_at).toLocaleString()}</p>
            </div>
            <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
              incident.status === 'open' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            }`}>
              {incident.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ token, onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <p className="text-gray-600 text-sm sm:text-base">Monitor the health of all your websites</p>
      </div>

      {/* Stats Grid - Mobile Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
        <StatsCard
          title="Total Monitors"
          value={stats?.total_monitors || 0}
          icon={Monitor}
          color="blue"
        />
        <StatsCard
          title="Online"
          value={stats?.up_monitors || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatsCard
          title="Offline"
          value={stats?.down_monitors || 0}
          icon={AlertTriangle}
          color="red"
        />
        <StatsCard
          title="Uptime"
          value={`${stats?.uptime_percentage || 100}%`}
          icon={TrendingUp}
          color="green"
          subtitle="Last 24 hours"
        />
      </div>

      {/* Recent Incidents and Quick Actions - Mobile Stack */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        <RecentIncidents incidents={stats?.recent_incidents} />
        
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button 
              onClick={() => onNavigate && onNavigate('monitors')}
              className="w-full flex items-center space-x-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
            >
              <Plus className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="font-medium text-blue-900">Add New Monitor</span>
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('settings')}
              className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
            >
              <Settings className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <span className="font-medium text-gray-900">Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
